import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SessionStorageService } from '../services/session-storage.service';
import {
  BusinessEventAction,
  BusinessEventActionChannel,
  BusinessEventActionExecution,
  BusinessEventAlertSimulationRequest,
  BusinessEventAlertSimulationResponse,
  BusinessEventDefinition,
  BusinessEventDefinitionContext,
  BusinessEventExecutionSearchOptions,
  BusinessEventNotificationTeam,
  BusinessEventRecipientEmployee,
  BusinessEventRenderTestRequest,
  BusinessEventRenderTestResponse,
  BusinessEventRule,
  BusinessEventTemplate,
  PageResponse
} from './business-events.model';

@Injectable({ providedIn: 'root' })
export class BusinessEventsService {
  private readonly http = inject(HttpClient);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly baseUrl = `${environment.restApiUrl}business-events`;

  getCompanyId(): number {
    return this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
  }

  getDefinitions(): Observable<BusinessEventDefinition[]> {
    return this.http.get<BusinessEventDefinition[]>(`${this.baseUrl}/definitions`);
  }

  getDefinitionContext(eventCode: string, companyId = this.getCompanyId()): Observable<BusinessEventDefinitionContext> {
    return this.http.get<BusinessEventDefinitionContext>(`${this.baseUrl}/definitions/${encodeURIComponent(eventCode)}/context`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  saveDefinition(definition: BusinessEventDefinition): Observable<BusinessEventDefinition> {
    return definition.id
      ? this.http.put<BusinessEventDefinition>(`${this.baseUrl}/definitions/${definition.id}`, definition)
      : this.http.post<BusinessEventDefinition>(`${this.baseUrl}/definitions`, definition);
  }

  getRules(companyId = this.getCompanyId(), eventCode?: string): Observable<BusinessEventRule[]> {
    let params = new HttpParams();
    if (companyId) params = params.set('companyId', String(companyId));
    if (eventCode?.trim()) params = params.set('eventCode', eventCode.trim());
    return this.http.get<BusinessEventRule[]>(`${this.baseUrl}/rules`, { params });
  }

  saveRule(rule: BusinessEventRule): Observable<BusinessEventRule> {
    const payload: BusinessEventRule = {
      ...rule,
      companyId: rule.companyId || this.getCompanyId()
    };
    return payload.id
      ? this.http.put<BusinessEventRule>(`${this.baseUrl}/rules/${payload.id}`, payload)
      : this.http.post<BusinessEventRule>(`${this.baseUrl}/rules`, payload);
  }

  getAlerts(companyId = this.getCompanyId(), eventCode?: string): Observable<BusinessEventRule[]> {
    let params = new HttpParams();
    if (companyId) params = params.set('companyId', String(companyId));
    if (eventCode?.trim()) params = params.set('eventCode', eventCode.trim());
    return this.http.get<BusinessEventRule[]>(`${this.baseUrl}/alerts`, { params });
  }

  saveAlert(alert: BusinessEventRule): Observable<BusinessEventRule> {
    const payload = { ...alert, companyId: alert.companyId || this.getCompanyId() };
    return payload.id
      ? this.http.put<BusinessEventRule>(`${this.baseUrl}/alerts/${payload.id}`, payload)
      : this.http.post<BusinessEventRule>(`${this.baseUrl}/alerts`, payload);
  }

  getNotificationTeams(companyId = this.getCompanyId()): Observable<BusinessEventNotificationTeam[]> {
    return this.http.get<BusinessEventNotificationTeam[]>(`${this.baseUrl}/teams`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  saveNotificationTeam(team: BusinessEventNotificationTeam): Observable<BusinessEventNotificationTeam> {
    const payload = { ...team, companyId: team.companyId || this.getCompanyId() };
    return payload.id
      ? this.http.put<BusinessEventNotificationTeam>(`${this.baseUrl}/teams/${payload.id}`, payload)
      : this.http.post<BusinessEventNotificationTeam>(`${this.baseUrl}/teams`, payload);
  }

  getRecipientEmployees(companyId = this.getCompanyId()): Observable<BusinessEventRecipientEmployee[]> {
    return this.http.get<BusinessEventRecipientEmployee[]>(`${this.baseUrl}/recipient-employees`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  publishAlert(alertId: number): Observable<BusinessEventRule> {
    return this.http.post<BusinessEventRule>(`${this.baseUrl}/alerts/${alertId}/publish`, {}, {
      params: new HttpParams().set('companyId', String(this.getCompanyId()))
    });
  }

  pauseAlert(alertId: number): Observable<BusinessEventRule> {
    return this.http.post<BusinessEventRule>(`${this.baseUrl}/alerts/${alertId}/pause`, {}, {
      params: new HttpParams().set('companyId', String(this.getCompanyId()))
    });
  }

  simulateAlert(request: BusinessEventAlertSimulationRequest): Observable<BusinessEventAlertSimulationResponse> {
    return this.http.post<BusinessEventAlertSimulationResponse>(`${this.baseUrl}/alerts/simulate`, {
      ...request,
      companyId: request.companyId || this.getCompanyId()
    });
  }

  saveAction(action: BusinessEventAction): Observable<BusinessEventAction> {
    if (!action.ruleId) {
      throw new Error('ruleId is required to save a business event action.');
    }
    return action.id
      ? this.http.put<BusinessEventAction>(`${this.baseUrl}/actions/${action.id}`, action, {
          params: new HttpParams().set('companyId', String(this.getCompanyId()))
        })
      : this.http.post<BusinessEventAction>(`${this.baseUrl}/rules/${action.ruleId}/actions`, action, {
          params: new HttpParams().set('companyId', String(this.getCompanyId()))
        });
  }

  getTemplates(companyId = this.getCompanyId(), templateKey?: string): Observable<BusinessEventTemplate[]> {
    let params = new HttpParams();
    params = params.set('companyId', String(companyId));
    if (templateKey?.trim()) params = params.set('templateKey', templateKey.trim());
    return this.http.get<BusinessEventTemplate[]>(`${this.baseUrl}/templates`, { params });
  }

  saveTemplate(template: BusinessEventTemplate): Observable<BusinessEventTemplate> {
    const payload = { ...template, companyId: template.companyId || this.getCompanyId() };
    return payload.id
      ? this.http.put<BusinessEventTemplate>(`${this.baseUrl}/templates/${payload.id}`, payload)
      : this.http.post<BusinessEventTemplate>(`${this.baseUrl}/templates`, payload);
  }

  renderTest(request: BusinessEventRenderTestRequest): Observable<BusinessEventRenderTestResponse> {
    return this.http.post<BusinessEventRenderTestResponse>(`${this.baseUrl}/render-test`, request);
  }

  getExecutions(options: BusinessEventExecutionSearchOptions = {}): Observable<PageResponse<BusinessEventActionExecution>> {
    let params = new HttpParams()
      .set('page', String(options.page ?? 0))
      .set('size', String(options.size ?? 50));
    const companyId = options.companyId ?? this.getCompanyId();
    if (companyId) params = params.set('companyId', String(companyId));
    if (options.status) params = params.set('status', options.status);
    if (options.eventCode?.trim()) params = params.set('eventCode', options.eventCode.trim());
    return this.http.get<PageResponse<BusinessEventActionExecution>>(`${this.baseUrl}/executions`, { params });
  }

  retryExecution(executionId: number): Observable<BusinessEventActionExecution> {
    return this.http.post<BusinessEventActionExecution>(`${this.baseUrl}/executions/${executionId}/retry`, {}, {
      params: new HttpParams().set('companyId', String(this.getCompanyId()))
    });
  }
}
